import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { RouteLoading } from '../components/elements/route-loading/RouteLoading';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useNavigate } from '../hooks/use-router/use-router';

// Import pages with lazy loading for code splitting
const ErrorPage = React.lazy(() => import('../pages/ErrorPage'));
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const ForgottenPasswordPage = React.lazy(
  () => import('../pages/ForgottenPasswordPage')
);
const MfaSetupPage = React.lazy(() => import('../pages/MfaSetupPage'));
const SessionExpiredPage = React.lazy(
  () => import('../pages/SessionExpiredPage')
);
const SignUpPage = React.lazy(() => import('../pages/SignUpPage'));
const EnterInvitationCodePage = React.lazy(
  () => import('../pages/EnterInvitationCodePage')
);
const ResendInvitationCodePage = React.lazy(
  () => import('../pages/ResendInvitationCodePage')
);
const SsoRedirectPage = React.lazy(() => import('../pages/SsoRedirectPage'));
const TestRoutePage = React.lazy(() => import('../pages/TestRoutePage'));

/**
 * iOS-safe redirect component that preserves query parameters.
 * Uses our custom useNavigate hook which maintains iOS-compatible URL encoding
 * (spaces as %20, not +) when redirecting from root to /login.
 */
const RedirectToLogin: React.FC = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    // Custom navigate automatically preserves query params with iOS-safe encoding
    navigate('/login', { replace: true });
  }, [navigate]);

  // Show loading while redirecting
  return <RouteLoading />;
};

export const AppRouter: React.FC = () => {
  return (
    <ErrorBoundary>
      <React.Suspense fallback={<RouteLoading />}>
        <Routes>
          {/* Default route - redirect to login with iOS-safe param preservation */}
          <Route path='/' element={<RedirectToLogin />} />

          {/* Main routes */}
          <Route path='/login' element={<LoginPage />} />
          <Route path='/error' element={<ErrorPage />} />
          <Route
            path='/forgotten-password'
            element={<ForgottenPasswordPage />}
          />
          <Route path='/mfa-setup' element={<MfaSetupPage />} />
          <Route path='/session-expired' element={<SessionExpiredPage />} />
          <Route path='/sign-up' element={<SignUpPage />} />
          <Route
            path='/sign-up/enter-invitation-code'
            element={<EnterInvitationCodePage />}
          />
          <Route
            path='/sign-up/resend-invitation-code'
            element={<ResendInvitationCodePage />}
          />
          <Route path='/sso-redirect' element={<SsoRedirectPage />} />
          <Route path='/test-route' element={<TestRoutePage />} />

          {/* Catch all - redirect to error */}
          <Route path='*' element={<Navigate to='/error' replace />} />
        </Routes>
      </React.Suspense>
    </ErrorBoundary>
  );
};
